import React from 'react';
import { createSmoothPath, BOARD_WIDTH, BOARD_HEIGHT, getPointerCoords } from '../utils/whiteboardUtils';
import WhiteboardShapeList from './WhiteboardShapeList';

export default function WhiteboardCanvas({
  svgRef, panRef, pan,
  shapes, setShapes, selectedShape, setSelectedShape,
  tool, isViewOnly, isStudent,
  drawingShape,
  currentLine,
  fadedShapes,
  eraserShadow, isErasing, eraserSize,
  shapeToolbar, setShapeToolbar,
  editingStickerId, setEditingStickerId,
  editingTextId, setEditingTextId,
  textSizes, setTextSizes,
  setDragged,
  draggedSticker,
  setIsResizing, setOriginalSize, setResizeStart, setResizing, setStartShapes,
  stickerEditRef,
  centerOnExercise,
  dragPanStart, dragPanMove, dragPanEnd,
  handlePointerDown, handlePointerMove, handlePointerUp,
  handleBoardMouseDown,
  handleYouTubeClick,
}) {
  const safeScale = Math.max(panRef.current.scale, 0.1);
  const vbX = parseInt(-panRef.current.x / safeScale);
  const vbY = parseInt(-panRef.current.y / safeScale);
  const vbW = parseInt(window.innerWidth / safeScale);
  const vbH = parseInt(window.innerHeight / safeScale);
  const viewBox = (isNaN(vbX) || isNaN(vbY) || isNaN(vbW) || isNaN(vbH) ||
    !isFinite(vbX) || !isFinite(vbY) || !isFinite(vbW) || !isFinite(vbH))
    ? '0 0 1000 1000'
    : `${vbX} ${vbY} ${vbW} ${vbH}`;

  return (
    <div
      style={{ position: 'absolute', left: 0, top: 0, width: '100vw', height: '100vh', background: 'transparent', borderRadius: 0, overflow: 'hidden', zIndex: 1, touchAction: 'none' }}
      onMouseDown={dragPanStart}
      onMouseMove={dragPanMove}
      onMouseUp={dragPanEnd}
      onMouseLeave={dragPanEnd}
      onTouchStart={dragPanStart}
      onTouchMove={dragPanMove}
      onTouchEnd={dragPanEnd}
    >
      <svg
        ref={svgRef}
        width={window.innerWidth}
        height={window.innerHeight}
        viewBox={viewBox}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          zIndex: 1,
          pointerEvents: 'auto',
          touchAction: 'none',
          userSelect: 'none',
        }}
        onMouseDown={(e) => {
          if (shapeToolbar) setShapeToolbar(null);
          handlePointerDown(e);
        }}
        onTouchStart={(e) => {
          if (e.touches.length > 1) return;
          if (shapeToolbar) setShapeToolbar(null);
          handlePointerDown(e);
        }}
        onMouseMove={handlePointerMove}
        onTouchMove={(e) => {
          if (e.touches.length > 1) return;
          handlePointerMove(e);
        }}
        onMouseUp={handlePointerUp}
        onTouchEnd={handlePointerUp}
      >
        <rect
          x="0"
          y="0"
          width={BOARD_WIDTH}
          height={BOARD_HEIGHT}
          fill="transparent"
          onMouseDown={(e) => {
            if (shapeToolbar) setShapeToolbar(null);
            handleBoardMouseDown(e);
          }}
          onTouchStart={(e) => {
            if (e.touches.length > 1) return;
            if (shapeToolbar) setShapeToolbar(null);
            handleBoardMouseDown(e);
          }}
        />
        <g>
          <WhiteboardShapeList
            shapes={shapes}
            selectedShape={selectedShape}
            setSelectedShape={setSelectedShape}
            tool={tool}
            isViewOnly={isViewOnly}
            isStudent={isStudent}
            getPointerCoords={getPointerCoords}
            panRef={panRef}
            svgRef={svgRef}
            setDragged={setDragged}
            fadedShapes={fadedShapes}
            setShapeToolbar={setShapeToolbar}
            setShapes={setShapes}
            handleYouTubeClick={handleYouTubeClick}
            pan={pan}
            editingStickerId={editingStickerId}
            editingTextId={editingTextId}
            setEditingStickerId={setEditingStickerId}
            setEditingTextId={setEditingTextId}
            setIsResizing={setIsResizing}
            setOriginalSize={setOriginalSize}
            setResizeStart={setResizeStart}
            setResizing={setResizing}
            setStartShapes={setStartShapes}
            stickerEditRef={stickerEditRef}
            draggedSticker={draggedSticker}
            textSizes={textSizes}
            setTextSizes={setTextSizes}
            centerOnExercise={centerOnExercise}
          />

          {/* Preview of shape being drawn */}
          {drawingShape && (() => {
            if (drawingShape.type === 'rect') {
              return (
                <rect
                  x={drawingShape.x}
                  y={drawingShape.y}
                  width={drawingShape.width}
                  height={drawingShape.height}
                  fill="none"
                  stroke="#000"
                  strokeWidth={1}
                  rx={12}
                  strokeDasharray="3,3"
                />
              );
            } else if (drawingShape.type === 'circle') {
              return (
                <circle
                  cx={drawingShape.x}
                  cy={drawingShape.y}
                  r={drawingShape.r}
                  fill="none"
                  stroke="#000"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                />
              );
            } else if (drawingShape.type === 'triangle') {
              const p1 = [drawingShape.x + drawingShape.width/2, drawingShape.y];
              const p2 = [drawingShape.x + drawingShape.width, drawingShape.y + drawingShape.height];
              const p3 = [drawingShape.x, drawingShape.y + drawingShape.height];
              return (
                <polygon
                  points={`${p1.join(',')} ${p2.join(',')} ${p3.join(',')}`}
                  fill="none"
                  stroke="#000"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                />
              );
            } else if (drawingShape.type === 'arrow') {
              const dx = drawingShape.x2 - drawingShape.x1, dy = drawingShape.y2 - drawingShape.y1;
              const angle = Math.atan2(dy, dx);
              const headlen = 18;
              const arrowX = drawingShape.x2 - headlen * Math.cos(angle);
              const arrowY = drawingShape.y2 - headlen * Math.sin(angle);
              return (
                <g>
                  <line x1={drawingShape.x1} y1={drawingShape.y1} x2={arrowX} y2={arrowY} stroke="#000" strokeWidth={1} strokeDasharray="3,3" />
                  <polygon points={`
                    ${drawingShape.x2},${drawingShape.y2}
                    ${arrowX + 7*Math.sin(angle)},${arrowY - 7*Math.cos(angle)}
                    ${arrowX - 7*Math.sin(angle)},${arrowY + 7*Math.cos(angle)}
                  `} fill="#000" />
                </g>
              );
            } else if (drawingShape.type === 'line') {
              return (
                <line x1={drawingShape.x1} y1={drawingShape.y1} x2={drawingShape.x2} y2={drawingShape.y2} stroke="#000" strokeWidth={1} strokeDasharray="3,3" />
              );
            } else if (drawingShape.type === 'polygon') {
              const points = Array.from({length: drawingShape.sides}).map((_, i) => {
                const angle = (Math.PI * 2 * i) / drawingShape.sides - Math.PI/2;
                return [drawingShape.x + drawingShape.r * Math.cos(angle), drawingShape.y + drawingShape.r * Math.sin(angle)];
              });
              return (
                <polygon
                  points={points.map(p => p.join(",")).join(' ')}
                  fill="none"
                  stroke="#000"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                />
              );
            } else if (drawingShape.type === 'message') {
              return (
                <g>
                  <rect x={drawingShape.x} y={drawingShape.y} width={drawingShape.width} height={drawingShape.height} rx={14} fill="none" stroke="#000" strokeWidth={1} strokeDasharray="3,3" />
                  <polygon points={[
                    [drawingShape.x + 24, drawingShape.y + drawingShape.height],
                    [drawingShape.x + 44, drawingShape.y + drawingShape.height],
                    [drawingShape.x + 34, drawingShape.y + drawingShape.height + 18],
                  ].map(p => p.join(",")).join(' ')} fill="#000" />
                </g>
              );
            }
            return null;
          })()}

          {/* Current pen/highlighter stroke */}
          {currentLine && currentLine.points.length > 1 && (
            <path
              d={createSmoothPath(currentLine.points, currentLine.thickness)}
              fill={currentLine.color}
              stroke="none"
              opacity={currentLine.alpha ?? 1}
            />
          )}

          {/* Eraser shadow trail */}
          {tool === 'eraser' && isErasing && eraserShadow.length > 1 && (
            <>
              {eraserShadow.map((point, index) => {
                if (index === 0) return null;
                const opacity = (index / eraserShadow.length) * 0.8;
                const width = (index / eraserShadow.length) * eraserSize;
                return (
                  <circle
                    key={`tail-${index}`}
                    cx={point.x}
                    cy={point.y}
                    r={width / 2}
                    fill="rgba(128, 128, 128, 0.6)"
                    opacity={opacity}
                    style={{ pointerEvents: 'none' }}
                  />
                );
              })}
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
